import { ref } from 'vue';

export function usePullToRefresh() {
  const isRefreshing = ref(false);
  const lastRefreshTime = ref(null);

  // Función para crear un handler de refresh con debounce
  const createRefreshHandler = (refreshFunctions, options = {}) => {
    const {
      minInterval = 2000, // Mínimo tiempo entre refreshes (2 segundos)
      showLoading = true,
      onStart = null,
      onSuccess = null,
      onError = null
    } = options;

    return async () => {
      // Verificar si ha pasado suficiente tiempo desde el último refresh
      const now = Date.now();
      if (lastRefreshTime.value && (now - lastRefreshTime.value) < minInterval) {
        console.log('⏱️ Refresh ignorado - muy frecuente');
        return;
      }

      if (isRefreshing.value) {
        console.log('⏱️ Refresh ignorado - ya en progreso');
        return;
      }

      console.log('🔄 Iniciando pull-to-refresh...');
      
      isRefreshing.value = true;
      lastRefreshTime.value = now;

      try {
        // Ejecutar función de inicio si existe
        if (onStart) {
          await onStart();
        }

        // Ejecutar todas las funciones de refresh
        const results = await Promise.allSettled(
          Array.isArray(refreshFunctions) ? refreshFunctions : [refreshFunctions]
        );

        // Verificar si todas las funciones fueron exitosas
        const hasErrors = results.some(result => result.status === 'rejected');
        
        if (hasErrors) {
          const errors = results
            .filter(result => result.status === 'rejected')
            .map(result => result.reason);
          
          console.error('❌ Errores durante el refresh:', errors);
          
          if (onError) {
            await onError(errors);
          }
          
          throw new Error('Algunas funciones de refresh fallaron');
        }

        console.log('✅ Pull-to-refresh completado exitosamente');
        
        if (onSuccess) {
          await onSuccess(results.map(r => r.value));
        }

      } catch (error) {
        console.error('❌ Error en pull-to-refresh:', error);
        
        if (onError) {
          await onError([error]);
        }
        
        throw error;
      } finally {
        isRefreshing.value = false;
      }
    };
  };

  // Función para crear un handler simple para una sola función
  const createSimpleRefreshHandler = (refreshFunction, options = {}) => {
    return createRefreshHandler([refreshFunction], options);
  };

  // Función para crear un handler que recarga la página completa
  const createPageReloadHandler = (options = {}) => {
    const defaultOptions = {
      minInterval: 1000, // 1 segundo mínimo entre recargas
      onStart: () => console.log('🔄 Recargando página...'),
      onSuccess: () => console.log('✅ Página recargada'),
      onError: (errors) => {
        console.error('❌ Error al recargar página:', errors);
        // Si hay error, forzar recarga
        window.location.reload();
      },
      ...options
    };

    return createRefreshHandler([() => window.location.reload()], defaultOptions);
  };

  // Función para crear un handler para recargar datos de cuentos
  const createCuentosRefreshHandler = (cuentosFunctions, options = {}) => {
    const defaultOptions = {
      minInterval: 3000, // 3 segundos para cuentos
      onStart: () => console.log('🔄 Recargando cuentos...'),
      onSuccess: () => console.log('✅ Cuentos recargados'),
      onError: (errors) => console.error('❌ Error al recargar cuentos:', errors),
      ...options
    };

    return createRefreshHandler(cuentosFunctions, defaultOptions);
  };

  return {
    isRefreshing,
    lastRefreshTime,
    createRefreshHandler,
    createSimpleRefreshHandler,
    createPageReloadHandler,
    createCuentosRefreshHandler
  };
} 